<script setup>
import { ref, computed, onMounted } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { usePlanningStore } from '@/stores/planning.store'

import { useNotify } from '@/composables/notify'
import { useIsUpdated } from '@/composables/object'
import { useNavigator } from '@/composables/navigator'
import { useEscapeClose } from '@/composables/keyboard'
import { useDetectDevice } from '@/composables/device'
import { useArchive } from '@/composables/archive'
import { useClone } from '@/composables/object'
import { useStepper } from '@/composables/stepper'
import { useCompanyResolve } from '@/composables/company-resolve'

import NavigateBack from '@/components/shared/NavigateBack.vue'
import StepperNavigation from '@/components/shared/StepperNavigation.vue'
import ArchiveWarningDialog from '@/components/shared/ArchiveWarningDialog.vue'
import CompanyCombobox from '@/components/shared/CompanyCombobox.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
})

const i18n = getI18n()
const { isMobile } = useDetectDevice()
const { notifySuccess, notifyError } = useNotify()
const { step, nextStep, prevStep, markAttempted, wasAttempted } = useStepper()
const { resolveCompany, rollbackCompany } = useCompanyResolve()

const planningStore = usePlanningStore()

const form = ref(null)
const editItem = ref(Object.keys(props.modelValue).length > 0 ? useClone(props.modelValue) : {})

const { isUpdated, isUpdating } = useIsUpdated(editItem)

onMounted(async () => {
  if (!editItem.value.id) {
    editItem.value.code = String(await planningStore.fetchNextListingIdAsync())
  }
})

const { archiveWarning, archiveAsync, onRowRemoving: onDelete } = useArchive({
  onArchive: async (id) => { await planningStore.archiveListingAsync(id) },
  onDelete: async (id, config) => {
    await planningStore.deleteListingAsync(id, config)
    notifySuccess(i18n.t('info.deletedSuccessfully'))
    emit('delete')
  },
})

const updateListingAsync = async () => {
  const typedCompany = editItem.value.company
  let createdCompanyId = null
  try {
    const { id, createdId } = await resolveCompany(typedCompany)
    createdCompanyId = createdId
    editItem.value.company = id
    const saved = await planningStore.upsertListingAsync({ ...editItem.value })
    notifySuccess(editItem.value.id ? i18n.t('info.updatedSuccessfully') : i18n.t('info.createdSuccessfully'))
    editItem.value.id = editItem.value.id || saved.data.id
    emit('update', editItem.value)
  } catch (error) {
    await rollbackCompany(createdCompanyId)
    editItem.value.company = typedCompany
    console.error(error)
    notifyError(i18n.t('error.saveFailed'))
  }
}

const onUpdate = async () => {
  isUpdating.value = true
  await updateListingAsync()
  isUpdating.value = false
}

const showDeleteDialog = ref(false)
const onConfirmDelete = () => { showDeleteDialog.value = true }

const emit = defineEmits(['update', 'cancel', 'delete'])
useNavigator(() => emit('cancel'))
useEscapeClose(isUpdated, () => emit('cancel'))
const onCancel = () => emit('cancel')

const step1HasError = computed(() => !editItem.value.code || !editItem.value.name)
const hasErrors = computed(() => step1HasError.value)
</script>

<template>
  <q-card v-drag-dialog="!isMobile" class="dialog-card-flex">
    <archive-warning-dialog
      v-model="archiveWarning"
      @confirm="async () => {
        await archiveAsync(archiveWarning.item.id, false)
        emit('update')
      }"
    />
    <confirm-dialog
      v-model="showDeleteDialog"
      :message="i18n.t('common.confirmDelete')"
      icon="mdi-delete-outline"
      icon-color="negative"
      @confirm="onDelete({ id: editItem.id })"
    />
    <navigate-back
      :text="editItem?.id ? i18n.t('common.editListing') : i18n.t('common.addListing')"
      @cancel="onCancel"
    />
    <q-card-section class="scroll col q-pa-none dialog-card-content-scroll" :class="{ 'dialog-card-content': editItem?.id }">
      <q-card-section class="q-px-sm q-pt-none">
        <q-form ref="form">
          <q-stepper v-model="step" class="q-py-none">
            <q-step
              :name="1"
              :title="i18n.t('common.basicInfo')"
              :icon="wasAttempted(1) && step1HasError ? 'mdi-alert' : 'mdi-folder-plus'"
              :done="!!editItem.id || step > 1"
              :header-nav="!!editItem.id || step > 1"
              :error="wasAttempted(1) && step1HasError"
              error-color="negative"
            >
              <div class="row q-col-gutter-sm">
                <q-input
                  v-model="editItem.code"
                  :class="isMobile ? 'col-4' : 'col-2'"
                  :label="i18n.t('common.code')"
                  required
                  :rules="[val => (val && val.length > 0) || i18n.t('common.required')]"
                />
                <q-input
                  v-model="editItem.name"
                  :class="isMobile ? 'col-8' : 'col-6'"
                  :label="i18n.t('common.name')"
                  autofocus
                  required
                  :rules="[val => (val && val.length > 0) || i18n.t('common.required')]"
                />
                <company-combobox
                  v-model="editItem.company"
                  :class="isMobile ? 'col-12' : 'col-4'"
                />
              </div>
              <stepper-navigation
                :continue="() => { markAttempted(1); form.validate().then(success => !step1HasError && success && nextStep()) }"
              />
            </q-step>
            <q-step
              :name="2"
              :title="i18n.t('common.description')"
              icon="mdi-text"
              :done="!!editItem.id || step > 2"
              :header-nav="!!editItem.id || step > 2"
            >
              <div class="row q-col-gutter-sm">
                <q-input
                  v-model="editItem.description"
                  class="col-12"
                  :label="i18n.t('common.description')"
                  type="textarea"
                  autogrow
                />
              </div>
              <stepper-navigation
                :back="prevStep"
                :finish="editItem.id ? null : () => form.validate().then(success => !hasErrors && success && onUpdate())"
                :finish-label="i18n.t('common.addListing')"
                :loading="isUpdating"
              />
            </q-step>
          </q-stepper>
        </q-form>
      </q-card-section>
    </q-card-section>
    <q-card-actions v-if="editItem?.id" class="align-center dialog-card-bottom-bar">
      <q-btn
        class="large-btn q-mr-sm"
        color="negative"
        :label="i18n.t('common.delete')"
        @click="onConfirmDelete"
      />
      <q-btn
        v-if="isUpdated"
        class="large-btn"
        color="positive"
        :disable="hasErrors"
        :label="i18n.t('common.update')"
        :loading="isUpdating"
        @click="() => form.validate().then(success => !hasErrors && success && onUpdate())"
      />
    </q-card-actions>
  </q-card>
</template>

<style scoped>
</style>
